import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppSettings } from 'src/app/app-settings';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { PlacesService } from 'src/app/services/places.service';
import { PermissionService } from 'src/app/services/permission.service';
import { EventosService } from 'src/app/services/eventos.service';
import { MODULE_CASH_REGISTER, MODULE_SEASON_PASSES, ModulesService } from 'src/app/services/modules.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-side-menu-panel',
  templateUrl: './side-menu-panel.component.html',
  styleUrls: ['./side-menu-panel.component.scss']
})
export class SideMenuPanelComponent implements OnInit {
  @Input() itemActive = 1;
  @Output() changeTab: EventEmitter<any> = new EventEmitter<any>();

  rolesProfile: any[] = this.auth
    .getProfile()
    ?.roles.map((role: any) => role.name);

  admin: boolean = false;
  allowOrder: boolean = false;
  allowSales: boolean = false;
  allowMarketing: boolean = false;
  allowSuperAdmin: boolean = false;
  showEventSubmenu: boolean = false;
  eventSubmenuCollapsed: boolean = true;
  isDraftEvent: boolean = false;
  eventId: number | null = null;
  eventType: 'informative' | 'external' | 'normal' | null = null;

  showVenueSubmenu: boolean = false;
  venueSubmenuCollapsed: boolean = true;
  venueId: number | null = null;
  venueMapVersion: boolean = false;
  isMapActive: boolean = false;

  showPermissionSubmenu: boolean = false;
  permissionRoles: any[] = [];
  currentPermissionRole: any = 'all';
  isOrgSubmenuExpanded: boolean = false;

  places: any[] = [];
  placeSelect: any = null;
  loadingPlaces: boolean = true;

  // Módulos contratados por la empresa (ver ModulesService)
  showSeasonPasses: boolean = true;
  showCashRegister: boolean = true;

  private routerSubscription!: Subscription;
  private businessSubscription!: Subscription;
  private modulesSubscription!: Subscription;

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private placesServices: PlacesService,
    private apiBusiness: ApiBusinessService,
    private permissionService: PermissionService,
    private eventosService: EventosService,
    private modulesService: ModulesService,
  ) {}

  ngOnInit(): void {
    this.allowSuperAdmin = this.rolesProfile?.find(
      (element) => element == 'superadmin',
    );

    this.rolesProfile?.forEach((element) => {
      if (
        element == 'ACCESS_CLOUD' ||
        element == 'ACCOUNT_OWNER' ||
        element == 'ACCESS_EXTERN_EVENTS' ||
        element == 'ACCESS_ADMIN' ||
        element == 'ACCESS_MANAGER' ||
        element == 'admin' ||
        element == 'superadmin'
      ) {
        this.admin = true;
      }
      if (element == 'ACCESS_SALES') {
        this.allowSales = true;
      }
      if (
        element == 'ACCESS_SUPPORT' ||
        element == 'ACCESS_REPORTS' ||
        element == 'ACCESS_SALES'
      ) {
        this.allowOrder = true;
      }
      if (element == 'ACCESS_MARKETING' || element == 'ACCESS_SUPPORT') {
        this.allowMarketing = true;
      }
    });

    this.syncEventSubmenuFromUrl(this.router.url);
    this.syncVenueSubmenuFromUrl(this.router.url);
    this.syncPermissionSubmenuFromUrl(this.router.url);
    this.routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.syncEventSubmenuFromUrl(event.url);
        this.syncVenueSubmenuFromUrl(event.url);
        this.syncPermissionSubmenuFromUrl(event.url);
      }
    });

    this.getPlaces();

    this.modulesSubscription = this.modulesService.activeModules$.subscribe(() => {
      this.showSeasonPasses = this.modulesService.hasModule(MODULE_SEASON_PASSES);
      this.showCashRegister = this.modulesService.hasModule(MODULE_CASH_REGISTER);
    });

    this.businessSubscription = this.apiBusiness.businesSuperadmin.subscribe(company => {
      if (company) {
        this.placeSelect = null;
        this.loadingPlaces = true;
        this.places = [];
        this.getPlaces();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.businessSubscription) {
      this.businessSubscription.unsubscribe();
    }
    if (this.modulesSubscription) {
      this.modulesSubscription.unsubscribe();
    }
  }

  activeTab(tab: number, name: string) {
    this.itemActive = tab;
    this.changeTab.emit({ tab: tab, name: name });
  }

  getPlaces() {
    const companySelect = localStorage.getItem('business')
      ? JSON.parse(localStorage.getItem('business') || '{}')
      : null;

    if (this.allowSuperAdmin) {
      this.placesServices.getPlacesByCompany(companySelect?.id).subscribe({
        next: (res: any) => {
          this.places = res.data.data;
          this.loadingPlaces = false;
        },
      });
    } else {
      this.auth.getMyData().subscribe({
        next: (res: any) => {
          this.places = res.companies.flatMap((company: any) => company.spaces);
          this.loadingPlaces = false;

          if (this.places && this.places.length === 1) {
            this.placeSelect = this.places[0].id;
            this.changePlaceUser();
          }
        },
      });
    }

    this.placeSelect = localStorage.getItem('places')
      ? JSON.parse(localStorage.getItem('places') || 'null')
      : null;
    if (this.placeSelect) {
      this.changePlaceUser();
    } else {
      this.apiBusiness.setChanges(companySelect);
    }
  }

  changePlaceUser() {
    const prevRaw = localStorage.getItem('places');
    const previousId = prevRaw ? JSON.parse(prevRaw) : null;

    this.apiBusiness.setPlaceStorage(this.placeSelect || null);
    const place = this.placeSelect ? this.placeSelect : 'place';
    this.apiBusiness.setChanges(place);

    // Solo recargar la URL si el espacio realmente cambió
    if (previousId !== this.placeSelect) {
      const currentUrl = this.router.url;
      this.router.navigate([currentUrl.split('?')[0]], {
        queryParams: this.parseQueryParams(currentUrl),
        onSameUrlNavigation: 'reload',
        replaceUrl: true
      } as any);
    }
  }

  private parseQueryParams(url: string): Record<string, string> {
    const qs = url.includes('?') ? url.split('?')[1] : '';
    return Object.fromEntries(new URLSearchParams(qs).entries());
  }

  get isVenueSubmenuExpanded(): boolean {
    return this.showVenueSubmenu && !this.venueSubmenuCollapsed;
  }

  toggleVenueSubmenu(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.showVenueSubmenu) {
      return;
    }
    this.venueSubmenuCollapsed = !this.venueSubmenuCollapsed;
  }

  private syncVenueSubmenuFromUrl(url: string) {
    const cleanUrl = (url || '').split('?')[0];
    const previousShow = this.showVenueSubmenu;
    const isVenueDetail = cleanUrl.includes('/panel-control/config/venues/') &&
      !cleanUrl.endsWith('/panel-control/config/venues');

    this.showVenueSubmenu = isVenueDetail;
    this.isMapActive = cleanUrl.includes('/map-venue/') || cleanUrl.includes('/create-map/');

    // Sincronizar org submenu según la URL (excluyendo la página de ayuda)
    if (cleanUrl.includes('/panel-control/config/') && !cleanUrl.includes('/panel-control/config/help')) {
      this.isOrgSubmenuExpanded = true;
    } else if (!cleanUrl.includes('/panel-control/config/') || cleanUrl.includes('/panel-control/config/help')) {
      this.isOrgSubmenuExpanded = false;
    }

    if (!isVenueDetail) {
      this.venueId = null;
      this.venueSubmenuCollapsed = true;
      return;
    }

    if (!previousShow) {
      this.venueSubmenuCollapsed = false;
    }

    const pathParts = cleanUrl.split('/');
    const possibleId = Number(pathParts[pathParts.length - 1]);
    const newVenueId = Number.isNaN(possibleId) ? null : possibleId;

    // Si es el mismo venue, no resetear ni volver a llamar a la API
    if (newVenueId === this.venueId) {
      return;
    }

    this.venueMapVersion = false;
    this.venueId = newVenueId;

    if (this.venueId) {
      this.placesServices.getPlaceById(this.venueId).subscribe({
        next: (res) => {
          this.venueMapVersion = !!(res?.data?.map || res?.data?.map_version);
        },
        error: () => { this.venueMapVersion = false; }
      });
    } else {
      this.venueMapVersion = false;
    }
  }

  get isEventSubmenuExpanded(): boolean {
    return this.showEventSubmenu && !this.eventSubmenuCollapsed;
  }

  toggleEventSubmenu(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.showEventSubmenu) {
      return;
    }

    this.eventSubmenuCollapsed = !this.eventSubmenuCollapsed;
  }

  openTicketOffice() {
    if (!this.admin && !this.allowSales && !this.allowSuperAdmin) {
      return;
    }
    const token = localStorage.getItem('jwt_token');
    const business = JSON.parse(localStorage.getItem('business') || '{}');
    const id_business = business.id;
    const url = `${AppSettings.SALES}/login/${id_business}/${token}`;
    window.open(url, '_blank');
  }

  private syncPermissionSubmenuFromUrl(url: string) {
    const cleanUrl = (url || '').split('?')[0];
    const isPermission = cleanUrl.includes('/config/permission');
    this.showPermissionSubmenu = isPermission;

    if (isPermission && this.permissionRoles.length === 0) {
      this.permissionService.getRolesFunctions().subscribe({
        next: (res: any) => { this.permissionRoles = res.data || []; }
      });
    }

    const params = new URLSearchParams(url.includes('?') ? url.split('?')[1] : '');
    this.currentPermissionRole = params.get('role') || 'all';
  }

  toggleOrgSubmenu(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isOrgSubmenuExpanded = !this.isOrgSubmenuExpanded;
  }

  private syncEventSubmenuFromUrl(url: string) {
    const cleanUrl = (url || '').split('?')[0];
    const previousShowEventSubmenu = this.showEventSubmenu;
    const isEventDetail =
      cleanUrl.includes('/panel-control/events/detail/') ||
      cleanUrl.includes('/panel-control/events/draft/detail/');

    this.showEventSubmenu = isEventDetail;
    this.isDraftEvent = cleanUrl.includes('/panel-control/events/draft/detail/');

    if (!isEventDetail) {
      this.eventId = null;
      this.eventType = null;
      this.eventSubmenuCollapsed = true;
      return;
    }

    if (!previousShowEventSubmenu) {
      this.eventSubmenuCollapsed = false;
    }

    const pathParts = cleanUrl.split('/');
    const possibleId = Number(pathParts[pathParts.length - 1]);
    const newEventId = Number.isNaN(possibleId) ? null : possibleId;

    if (newEventId === this.eventId) {
      return;
    }

    this.eventType = null;
    this.eventId = newEventId;

    if (this.eventId) {
      this.eventosService.getEventById(this.eventId).subscribe({
        next: (res: any) => {
          this.eventType = res.data.informative ? 'informative' : (res.data.external ? 'external' : 'normal');
        }
      });
    }
  }
}
